import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Link2, MessageCircle, Instagram, Loader2, ImagePlus, Trash2, ArrowUp, ArrowDown, Save, X, Megaphone } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { useAdminT } from "./adminI18n";
import { PALETTES } from "./AppearanceTab";
import { DEFAULT_LINK_ITEMS } from "../../pages/LinksPage";
import { resolveMedia } from "../../siteContext";

const buildCaption = (c, origin) => {
  const desc = (c.description || "").split("\n")[0].slice(0, 160);
  const price = c.price > 0 ? `\n💛 الاستثمار: ${c.price} ${String(c.currency || "usd").toUpperCase()}` : "";
  return `✨ ${c.title}\n${c.subtitle ? c.subtitle + "\n" : ""}\n${desc}${desc ? "…" : ""}${price}\n\n🔗 للتسجيل والتفاصيل: ${origin}/course/${c.id}\n\n#TheMaya #التشافي #تطوير_الذات #قيادة_واعية`;
};

export const MarketingTab = () => {
  const { t } = useAdminT();
  const [courses, setCourses] = useState(null);
  const origin = window.location.origin;
  const [linkItems, setLinkItems] = useState(null);
  const [linksPalette, setLinksPalette] = useState("default");
  const [linksBg, setLinksBg] = useState("");
  const [savingLinks, setSavingLinks] = useState(false);
  const [promo, setPromo] = useState({ on: false, text: "", link: "", cta: "" });
  const [savingPromo, setSavingPromo] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null); // item index or "bg"
  const fileInputs = useRef({});

  useEffect(() => {
    axios.get(`${API}/courses`).then((res) => setCourses((Array.isArray(res.data) ? res.data : []).filter((c) => !c.hidden))).catch(() => setCourses([]));
    axios
      .get(`${API}/settings`)
      .then((res) => {
        setLinkItems(Array.isArray(res.data.links_items) && res.data.links_items.length ? res.data.links_items : DEFAULT_LINK_ITEMS);
        setLinksPalette(res.data.links_palette || "default");
        setLinksBg(res.data.links_bg || "");
        setPromo({
          on: !!res.data.promo_on,
          text: res.data.promo_text || "يبدأ في 20 أكتوبر | التسجيل مفتوح الآن",
          link: res.data.promo_link || "/p/online-workshop",
          cta: res.data.promo_cta || "اكتشف البرنامج",
        });
      })
      .catch(() => setLinkItems(DEFAULT_LINK_ITEMS));
  }, []);

  const uploadLinkImage = async (file, target) => {
    const data = new FormData();
    data.append("file", file);
    setUploadingFor(target);
    try {
      const res = await axios.post(`${API}/admin/images/upload`, data, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      if (target === "bg") setLinksBg(res.data.url);
      else setLinkItems((arr) => arr.map((it, i) => (i === target ? { ...it, image: res.data.url } : it)));
      toast.success(t("تم رفع الصورة"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingFor(null);
    }
  };

  const moveItem = (idx, dir) =>
    setLinkItems((arr) => {
      const next = [...arr];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return arr;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const saveLinks = async () => {
    setSavingLinks(true);
    try {
      const clean = linkItems
        .map((it, i) => ({ id: it.id || `link-${i}`, ar: (it.ar || "").trim(), en: (it.en || "").trim(), url: (it.url || "").trim(), image: it.image || "", icon: it.icon || "link" }))
        .filter((it) => it.ar && it.url);
      await axios.put(
        `${API}/admin/settings`,
        { links_items: clean, links_palette: linksPalette === "default" ? "" : linksPalette, links_bg: linksBg },
        { headers: authHeaders() }
      );
      setLinkItems(clean);
      toast.success(t("تم الحفظ — صفحة الروابط محدثة"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingLinks(false);
    }
  };

  const copy = (text, msg) => {
    navigator.clipboard.writeText(text);
    toast.success(msg || t("تم النسخ"));
  };

  const quickLinks = [
    { label: t("صفحة الروابط (بايو إنستغرام)"), url: `${origin}/links`, testid: "marketing-bio-link" },
    { label: t("الموقع الرئيسي"), url: origin, testid: "marketing-home-link" },
    { label: t("صفحة الهدية المجانية"), url: `${origin}/p/morning-routine`, testid: "marketing-gift-link" },
  ];

  return (
    <div data-testid="admin-marketing-tab">
      <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("التسويق وإنستغرام")}</h2>
      <p className="text-luxury-gray text-sm mb-8">{t("روابط ونصوص جاهزة للنشر — انسخي والصقي في منشوراتك وقصصك وإعلاناتك")}</p>

      <section className="bg-luxury-surface border border-luxury-gold/40 p-6 rounded-2xl mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Instagram className="w-5 h-5 text-luxury-gold" strokeWidth={1.5} />
          <h3 className="text-luxury-gold font-semibold">{t("ضعي هذا الرابط في بايو إنستغرام — كل روابطك في صفحة واحدة فاخرة")}</h3>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <code data-testid="marketing-bio-url" dir="ltr" className="flex-1 min-w-0 bg-luxury-black border border-luxury-border text-luxury-bone text-sm px-4 py-3 rounded-xl truncate">{origin}/links</code>
          <button data-testid="marketing-bio-copy" onClick={() => copy(`${origin}/links`)}
            className="flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-5 py-3 rounded-full hover:bg-luxury-goldLight transition-colors">
            <Copy className="w-4 h-4" strokeWidth={1.5} /> {t("نسخ")}
          </button>
        </div>
      </section>

      <section data-testid="links-manager" className="bg-luxury-surface border border-luxury-border p-6 rounded-2xl mb-8">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <div className="flex items-center gap-3">
            <Link2 className="w-5 h-5 text-luxury-gold" strokeWidth={1.5} />
            <h3 className="text-luxury-bone font-semibold">{t("إدارة صفحة الروابط")}</h3>
          </div>
          <button data-testid="links-save-btn" onClick={saveLinks} disabled={savingLinks || !linkItems}
            className="flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-50">
            {savingLinks ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />} {t("حفظ صفحة الروابط")}
          </button>
        </div>
        <p className="text-luxury-gray text-xs mb-6">{t("أضيفي، عدّلي، رتّبي — والتغييرات تظهر فوراً على صفحة /links بعد الحفظ")}</p>

        {linkItems === null ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-luxury-gold animate-spin" /></div>
        ) : (
          <div className="space-y-3 mb-6">
            {linkItems.map((it, i) => (
              <div key={it.id || i} data-testid={`link-item-${i}`} className="bg-luxury-black border border-luxury-border rounded-xl p-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <button data-testid={`link-image-${i}`} onClick={() => fileInputs.current[i]?.click()} title={t("صورة الرابط")}
                      className="w-11 h-11 rounded-xl border border-dashed border-luxury-border flex items-center justify-center overflow-hidden flex-shrink-0 hover:border-luxury-gold transition-colors">
                      {uploadingFor === i ? <Loader2 className="w-4 h-4 text-luxury-gold animate-spin" />
                        : it.image ? <img src={resolveMedia(it.image)} alt="" className="w-full h-full object-cover" />
                        : <ImagePlus className="w-4 h-4 text-luxury-gray" strokeWidth={1.5} />}
                    </button>
                    <input ref={(el) => (fileInputs.current[i] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLinkImage(e.target.files[0], i)} />
                    <div className="flex-1 min-w-[140px] space-y-2">
                      <input data-testid={`link-ar-${i}`} value={it.ar} onChange={(e) => setLinkItems((arr) => arr.map((x, xi) => (xi === i ? { ...x, ar: e.target.value } : x)))}
                        placeholder={t("النص بالعربية")} className="w-full bg-luxury-surface border border-luxury-border rounded-lg px-3 py-2 text-sm text-luxury-bone focus:outline-none focus:border-luxury-gold" />
                      <input data-testid={`link-en-${i}`} value={it.en} onChange={(e) => setLinkItems((arr) => arr.map((x, xi) => (xi === i ? { ...x, en: e.target.value } : x)))}
                        placeholder={t("النص بالإنجليزية")} className="w-full bg-luxury-surface border border-luxury-border rounded-lg px-3 py-2 text-sm text-luxury-bone focus:outline-none focus:border-luxury-gold" />
                      <input data-testid={`link-url-${i}`} value={it.url} onChange={(e) => setLinkItems((arr) => arr.map((x, xi) => (xi === i ? { ...x, url: e.target.value } : x)))}
                        placeholder="/p/sunset أو https://…" dir="ltr" className="w-full bg-luxury-surface border border-luxury-border rounded-lg px-3 py-2 text-sm text-luxury-bone focus:outline-none focus:border-luxury-gold" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <button data-testid={`link-up-${i}`} onClick={() => moveItem(i, -1)} className="p-1.5 text-luxury-gray hover:text-luxury-gold transition-colors"><ArrowUp className="w-4 h-4" strokeWidth={1.5} /></button>
                      <button data-testid={`link-down-${i}`} onClick={() => moveItem(i, 1)} className="p-1.5 text-luxury-gray hover:text-luxury-gold transition-colors"><ArrowDown className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                    <button data-testid={`link-delete-${i}`} onClick={() => setLinkItems((arr) => arr.filter((_, xi) => xi !== i))}
                      className="p-1.5 text-luxury-gray hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                  </div>
                  {it.image && (
                    <button data-testid={`link-image-remove-${i}`} onClick={() => setLinkItems((arr) => arr.map((x, xi) => (xi === i ? { ...x, image: "" } : x)))}
                      className="flex items-center gap-1 text-luxury-gray hover:text-luxury-gold text-[11px] mt-2 transition-colors">
                      <X className="w-3 h-3" /> {t("إزالة الصورة (تظهر الأيقونة بدلاً منها)")}
                    </button>
                  )}
                </div>
            ))}
            <button data-testid="links-add-btn" onClick={() => setLinkItems((arr) => [...arr, { id: `link-${Date.now()}`, ar: "", en: "", url: "", image: "", icon: "link" }])}
              className="w-full border border-dashed border-luxury-border rounded-xl py-3 text-sm text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold transition-colors">
              + {t("أضيفي رابطاً جديداً")}
            </button>
          </div>
        )}

        <div className="mb-6">
          <p className="text-luxury-bone text-sm font-semibold mb-3">{t("ألوان صفحة الروابط")}</p>
          <div className="flex items-center gap-3 flex-wrap">
            <button data-testid="links-palette-default" onClick={() => setLinksPalette("default")}
              className={`px-4 py-2 rounded-full text-xs border transition-colors ${linksPalette === "default" ? "border-luxury-gold text-luxury-gold" : "border-luxury-border text-luxury-gray hover:border-luxury-gold/60"}`}>
              {t("افتراضي (ألوان الموقع)")}
            </button>
            {PALETTES.map((p) => (
              <button key={p.id} data-testid={`links-palette-${p.id}`} onClick={() => setLinksPalette(p.id)} title={p.name}
                className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs transition-colors ${linksPalette === p.id ? "border-luxury-gold text-luxury-bone" : "border-luxury-border text-luxury-gray hover:border-luxury-gold/60"}`}>
                <span className="w-4 h-4 rounded-full border border-white/20" style={{ background: `linear-gradient(135deg, ${p.colors.bg} 50%, ${p.colors.accent} 50%)` }} />
                {p.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-2">
          <p className="text-luxury-bone text-sm font-semibold mb-3">{t("صورة الخلفية")}</p>
          <div className="flex items-center gap-4 flex-wrap">
            {linksBg ? (
              <div className="relative">
                <img src={resolveMedia(linksBg)} alt="" data-testid="links-bg-preview" className="w-28 h-16 object-cover rounded-xl border border-luxury-border" />
                <button data-testid="links-bg-remove" onClick={() => setLinksBg("")}
                  className="absolute -top-2 -end-2 w-5 h-5 rounded-full bg-luxury-black border border-luxury-border flex items-center justify-center text-luxury-gray hover:text-red-400">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <p className="text-luxury-gray text-xs">{t("بدون صورة خلفية")}</p>
            )}
            <button data-testid="links-bg-upload" onClick={() => fileInputs.current.bg?.click()} disabled={uploadingFor === "bg"}
              className="flex items-center gap-2 border border-luxury-border text-luxury-bone px-4 py-2 rounded-full text-xs hover:border-luxury-gold hover:text-luxury-gold transition-colors disabled:opacity-50">
              {uploadingFor === "bg" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" strokeWidth={1.5} />} {t("ارفعي صورة")}
            </button>
            <input ref={(el) => (fileInputs.current.bg = el)} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLinkImage(e.target.files[0], "bg")} />
          </div>
        </div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-6 rounded-2xl mb-8">
        <div className="flex items-center gap-3 mb-5">
          <Link2 className="w-5 h-5 text-luxury-gold" strokeWidth={1.5} />
          <h3 className="text-luxury-bone font-semibold">{t("روابط سريعة")}</h3>
        </div>
        <div className="space-y-3">
          {quickLinks.map((l) => (
            <div key={l.url} className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-luxury-gray text-sm">{l.label}</span>
              <button data-testid={l.testid} onClick={() => copy(l.url)} className="flex items-center gap-2 border border-luxury-border text-luxury-bone px-4 py-2 rounded-full text-sm hover:border-luxury-gold hover:text-luxury-gold transition-colors">
                <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> <span dir="ltr" className="truncate max-w-[220px]">{l.url}</span>
              </button>
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-luxury-gray text-sm flex items-center gap-2"><MessageCircle className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} /> {t("رابط الواتساب المباشر")}</span>
            <button data-testid="marketing-wa-copy" onClick={() => copy(`https://wa.me/96170526967`)} className="flex items-center gap-2 border border-luxury-border text-luxury-bone px-4 py-2 rounded-full text-sm hover:border-luxury-gold hover:text-luxury-gold transition-colors">
              <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> <span dir="ltr">wa.me/96170526967</span>
            </button>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-luxury-bone font-semibold mb-5">{t("نصوص جاهزة لكل دورة — انسخي وانشري")}</h3>
        {courses === null ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-luxury-gold animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {courses.map((c, i) => (
              <div key={c.id} data-testid={`marketing-course-${i}`} className="bg-luxury-surface border border-luxury-border p-5 rounded-2xl">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <p className="text-luxury-gold font-semibold">{c.title}</p>
                  <button data-testid={`marketing-course-copy-${i}`} onClick={() => copy(buildCaption(c, origin), t("تم نسخ النص مع الرابط"))}
                    className="flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-4 py-2 rounded-full hover:bg-luxury-goldLight transition-colors">
                    <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("نسخ النص مع الرابط")}
                  </button>
                </div>
                <pre data-testid={`marketing-caption-${i}`} className="whitespace-pre-wrap text-luxury-gray text-xs leading-relaxed bg-luxury-black border border-luxury-border rounded-xl p-4 font-sans">{buildCaption(c, origin)}</pre>
              </div>
            ))}
            {courses.length === 0 && <p className="text-luxury-gray text-center py-8">{t("لا توجد دورات بعد")}</p>}
          </div>
        )}
      </section>

      <section className="bg-luxury-surface border border-luxury-gold/40 p-6 rounded-2xl" data-testid="promo-bar-section">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            <Megaphone className="w-5 h-5 text-luxury-gold" strokeWidth={1.5} />
            <h3 className="text-luxury-gold font-semibold">{t("شريط الإعلان أعلى الموقع")}</h3>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" data-testid="promo-on" checked={promo.on} onChange={(e) => setPromo({ ...promo, on: e.target.checked })} className="w-5 h-5 accent-[#D1A05C]" />
            <span className="text-luxury-bone text-sm">{t("مفعّل")}</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <input data-testid="promo-text" value={promo.text} onChange={(e) => setPromo({ ...promo, text: e.target.value })} placeholder={t("يبدأ في 20 أكتوبر | التسجيل مفتوح الآن")}
            className="bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-luxury-gold" />
          <input dir="ltr" data-testid="promo-link" value={promo.link} onChange={(e) => setPromo({ ...promo, link: e.target.value })} placeholder="/p/online-workshop"
            className="bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-luxury-gold" />
          <input data-testid="promo-cta" value={promo.cta} onChange={(e) => setPromo({ ...promo, cta: e.target.value })} placeholder={t("اكتشف البرنامج")}
            className="bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-luxury-gold" />
        </div>
        <button data-testid="promo-save" disabled={savingPromo}
          onClick={async () => {
            setSavingPromo(true);
            try {
              await axios.put(`${API}/admin/settings`, { promo_on: promo.on, promo_text: promo.text, promo_link: promo.link, promo_cta: promo.cta }, { headers: authHeaders() });
              toast.success(t("تم الحفظ — الشريط محدث على الموقع"));
            } catch (e) { toast.error(formatApiError(e)); } finally { setSavingPromo(false); }
          }}
          className="flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-bold px-6 py-2.5 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
          {savingPromo && <Loader2 className="w-4 h-4 animate-spin" />} {t("حفظ شريط الإعلان")}
        </button>
      </section>
    </div>
  );
};
